import { useEffect, useState } from 'react';

export interface ActiveGridTabStackContent {
  id?: string;
}

export interface ActiveContentItemChangedEvent {
  parent?: {
    config?: {
      activeItemIndex?: number;
    };
  };
}

export interface ActiveGridTabStack {
  config: {
    activeItemIndex?: number;
    content?: ActiveGridTabStackContent[];
  };
  on: (event: string, handler: (component: ActiveContentItemChangedEvent) => void) => void;
  off: (event: string, handler: (component: ActiveContentItemChangedEvent) => void) => void;
}

export interface UseActiveGridTabParams {
  stack?: ActiveGridTabStack | null;
  stackIndex: number;
  gridId: string;
}

export interface UseActiveGridTabResult {
  isActiveTab: boolean;
  isCurrentGrid: boolean;
  activeIndex: number;
}

const useActiveGridTab = ({
  stack,
  stackIndex,
  gridId,
}: UseActiveGridTabParams): UseActiveGridTabResult => {
  const [activeIndex, setActiveIndex] = useState(stack?.config?.activeItemIndex ?? 0);

  useEffect(() => {
    if (!stack) {
      return undefined;
    }

    const onTabSwitch = (component: ActiveContentItemChangedEvent) => {
      const idx = component?.parent?.config?.activeItemIndex;
      if (idx !== undefined) {
        setActiveIndex(idx);
      }
    };

    setActiveIndex(stack.config.activeItemIndex ?? 0);
    stack.on('activeContentItemChanged', onTabSwitch);
    return () => {
      stack.off('activeContentItemChanged', onTabSwitch);
    };
  }, [stack]);

  const currentGridId = stack?.config?.content?.[stackIndex]?.id;
  const isActiveTab = activeIndex === stackIndex;
  const isCurrentGrid = isActiveTab && currentGridId === gridId;

  return { isActiveTab, isCurrentGrid, activeIndex };
};

export default useActiveGridTab;
